import React, { useEffect, useCallback, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { ErrorHandler, captureException, AUTHENTICATION_SCOPES } from '@playbookux/playbook-web-core';
import { useHistory } from 'react-router-dom';

import { Container, Loader } from './styles';
import ReportViewUI from '../report-view/report-view-ui';
import Tabs from '../../../constants/tabs';
import withProjectSidebar from '../../../components/with-project-sidebar';
import { useUserScope } from '../../../../core/hooks';

import { GET_REPORT_BY_PROJECT_ID, CREATE_REPORTS } from './graphql';

const DEFAULT_TITLE = 'Untitled Report';

const Report = (props) => {
  const history = useHistory();
  const [createReportError, setCreateReportError] = useState(null);

  const { project } = props;

  const { data, loading, error } = useQuery(GET_REPORT_BY_PROJECT_ID, {
    variables: { projectId: project.id },
  });

  const { reports } = data || {};
  const [createReports] = useMutation(CREATE_REPORTS);
  const scope = useUserScope();

  const handleRedirectEdit = useCallback((id) => {
    history.replace(`/projects/${project.id}/reports/${id}/edit`);
  }, [history, project.id]);

  const handleRedirectView = useCallback((id) => {
    history.replace(`/projects/${project.id}/reports/${id}/view`);
  }, [history, project.id]);

  const handleCreate = async () => {
    try {
      const { data: newData } = await createReports({
        variables: {
          projectId: project.id,
          attributes: {
            name: DEFAULT_TITLE,
            reportData: '',
          },
        },
      });
      const { id } = newData.createReport.report;
      handleRedirectEdit(id);
    } catch (err) {
      setCreateReportError(err);
      captureException('CREATE_REPORTS_FAILURE', err);
    }
  };

  useEffect(() => {
    if (scope === AUTHENTICATION_SCOPES.USER) {
      if (reports?.length > 0) {
        handleRedirectEdit(reports[0].id);
      } else if (reports?.length === 0) {
        handleCreate();
      }
    } else if (reports?.length > 0) {
      handleRedirectView(reports[0].id);
    }
  }, [reports]);

  if (loading) {
    return (
      <Container>
        <Loader data-loader="report-loader" />
      </Container>
    );
  }

  if (error || createReportError) {
    return (
      <ErrorHandler
        error={error || createReportError}
        defaultMessage="Error loading reports. Please check your internet connection."
      />
    );
  }

  return <ReportViewUI />;
};

Report.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
};

export default withProjectSidebar({ selectedTab: Tabs.PROJECT })(Report);
